import axios from 'axios';

// Runtime config: check window.RUNTIME_CONFIG first (for Docker deployments),
// then fall back to build-time env var, then default
const getApiUrl = () => {
  if (window.RUNTIME_CONFIG?.API_URL) {
    return window.RUNTIME_CONFIG.API_URL;
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

const baseURL = getApiUrl();

console.log('API Base URL:', baseURL);
console.log('Node Environment:', process.env.NODE_ENV);
console.log('Runtime Config:', window.RUNTIME_CONFIG);

const api = axios.create({
  baseURL,
  timeout: 300000, // 5 minutes timeout for large file uploads
  withCredentials: true,
  maxContentLength: 100 * 1024 * 1024, // 100MB
  maxBodyLength: 100 * 1024 * 1024 // 100MB
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    // Only add token if it exists and is not expired
    if (token) {
      try {
        // Basic token validation - check if it's a valid JWT format
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          // Decode the payload to check expiration
          const payload = JSON.parse(atob(tokenParts[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          
          // Check if token is expired
          if (payload.exp && payload.exp < currentTime) {
            console.warn('Token is expired, removing from storage');
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            localStorage.removeItem('username');
            // Don't add expired token to request
          } else {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } else {
          // Invalid token format, remove it
          console.warn('Invalid token format, removing from storage');
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          localStorage.removeItem('username');
        }
      } catch (error) {
        // Error decoding token, remove it
        console.warn('Error decoding token, removing from storage:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        localStorage.removeItem('username');
      }
    }
    
    // Only set Content-Type for non-FormData requests
    // FormData requests need to let the browser set the Content-Type with boundary
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    // If it's FormData, don't set Content-Type - browser will set it automatically
    
    console.log('API Request:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      hasToken: !!config.headers['Authorization'],
      isFormData: config.data instanceof FormData
    });
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Response Error:', {
      message: error.message,
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data
    });

    // Handle authentication errors (401 = unauthorized, 403 = forbidden)
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Authentication failed';
      
      // Clear authentication data regardless of whether token exists
      // This handles cases where token was expired and removed by interceptor
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('username');
      sessionStorage.clear();
      
      // Only redirect if not already on login page or home page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        // Redirect to login page
        window.location.href = '/login';
        
        // Show session expired message
        if (token || errorMessage.includes('Token') || errorMessage.includes('token') || errorMessage.includes('expired') || errorMessage.includes('invalid')) {
          alert('Your session has expired. Please log in again.');
        } else {
          alert('Please log in to continue.');
        }
      } else if (!token) {
        // No token and already on login/home page - just log the message
        console.log('401 error but no token found - user may need to login');
      }
    } else if (error.response?.status === 403) {
      // 403 means user is authenticated but doesn't have permission
      // Don't logout, just show error message
      console.log('Access denied (403): Insufficient permissions');
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'You do not have permission to access this resource';
      
      // Only show alert if not already showing an error
      if (!error.config?.skipErrorAlert) {
        alert(errorMessage);
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
